import React, { useEffect, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Sparkles, Send, Loader2, Wand2, MessageSquare, CheckCircle2, Lightbulb, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Slide-out AI Design / Layout / Content Assistant.
 *
 * Props:
 *  - open (bool)
 *  - onOpenChange (fn)
 *  - pageContext (string)   e.g. 'Splash Editor', 'Support page', 'Bookshelf'
 *  - currentData (object)   current field values for context
 *  - onApplySuggestion (fn) callback(suggestion={field,new_value,rationale})
 *                           if not provided, suggestions are read-only
 */
export default function AiAssistantPanel({
  open,
  onOpenChange,
  pageContext = 'Admin Panel',
  currentData = {},
  onApplySuggestion,
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const QUICK_CHIPS = [
    { id: 'tighten_hero',  label: 'Tighten the hero', text: 'My hero feels too long and soft. Rewrite the hero eyebrow, title, and subtitle so they hit harder. Show me the proposed new values.' },
    { id: 'punchier_cta',  label: 'Punchier CTA',     text: 'Rewrite my primary CTA button label and the line above it so they convert better. Keep the brand voice.' },
    { id: 'audit_page',    label: 'Audit this page',  text: 'Audit the current page values for weak copy, redundancy, and conversion blockers. List the top 3 issues and propose fixes I can apply.' },
    { id: 'design_tips',   label: 'Design tips',      text: 'Review the layout of this admin section and give me 3 design or layout suggestions specific to a premium black/white/gold LMS.' },
    { id: 'more_mick',     label: 'More Mick voice',  text: 'Re-tone any field that doesn\u2019t sound like Mickey Trivett HEADSPACE — raw, honest, no fluff. Propose specific rewrites.' },
  ];

  useEffect(() => {
    // Auto-scroll on new messages
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Reset draft when panel opens (but keep history across opens for continuity)
  useEffect(() => { if (open) setDraft(''); }, [open]);

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setDraft('');
    setLoading(true);
    try {
      const { data } = await api.post('/admin/ai/assistant', {
        messages: next,
        page_context: pageContext,
        current_data: currentData || {},
      });
      const reply = data?.reply || 'Done.';
      const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setMessages((m) => [...m, { role: 'assistant', content: reply, suggestions }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI request failed.';
      toast.error('Assistant failed', { description: String(msg).slice(0, 200) });
      setMessages((m) => [...m, { role: 'assistant', content: `Sorry — ${String(msg).slice(0, 180)}`, suggestions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (s) => {
    if (!onApplySuggestion) {
      toast.message('Read-only suggestion', { description: 'This panel is not connected to an editor — copy the value manually.' });
      return;
    }
    try {
      onApplySuggestion(s);
      toast.success('Applied to field.', { description: `${s.field} updated. Don\u2019t forget to Save.` });
    } catch (e) {
      toast.error('Could not apply', { description: String(e?.message || e).slice(0, 200) });
    }
  };

  const clearChat = () => {
    if (messages.length === 0) return;
    if (!window.confirm('Clear conversation history?')) return;
    setMessages([]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col bg-white text-ink border-l border-kc-strong"
        data-testid="ai-assistant-panel"
      >
        <SheetHeader className="px-5 py-4 border-b border-kc bg-ink text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[color:var(--kc-gold)] text-black inline-flex items-center justify-center">
              <Wand2 size={18} />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-white font-display uppercase tracking-wider text-base">AI Design Assistant</SheetTitle>
              <SheetDescription className="text-white/60 text-[11px] mt-0.5">{pageContext} · Brand-voice tuned</SheetDescription>
            </div>
            {messages.length > 0 && (
              <button
                type="button"
                onClick={clearChat}
                title="Clear conversation"
                aria-label="Clear conversation"
                className="text-white/60 hover:text-white p-2 rounded-md hover:bg-white/10 transition-colors"
                data-testid="ai-assistant-clear"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </SheetHeader>

        {/* Quick chips (only when empty conversation) */}
        {messages.length === 0 && (
          <div className="px-5 pt-4 pb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-kc font-semibold mb-2 inline-flex items-center gap-1">
              <Lightbulb size={12} className="text-[color:var(--kc-gold-2)]" /> Try one
            </div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_CHIPS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => send(c.text)}
                  disabled={loading}
                  className="px-3 py-1.5 text-xs rounded-full border border-kc-strong bg-white hover:bg-[color:var(--kc-gold)] hover:text-black hover:border-[color:var(--kc-gold)] transition-colors disabled:opacity-50"
                  data-testid={`ai-chip-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-kc leading-relaxed">
              Ask me to rewrite fields, audit copy, propose layout changes, or generate content from scratch. I see the current values on this page — I\u2019ll suggest specific replacements you can apply with one click.
            </p>
          </div>
        )}

        {/* Conversation */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-assistant-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[90%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-[color:var(--kc-gold)] text-black rounded-tr-sm'
                  : 'bg-surface-2 text-ink rounded-tl-sm border border-kc'
              }`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.role === 'assistant' && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
                  <div className="mt-3 space-y-2" data-testid={`ai-suggestions-${i}`}>
                    {m.suggestions.map((s, j) => (
                      <div key={j} className="bg-white border border-kc-strong rounded-md p-3 shadow-sm">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-[color:var(--kc-gold-2)] font-bold font-mono break-all">{s.field}</span>
                          <button
                            type="button"
                            onClick={() => handleApply(s)}
                            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-ink text-white text-[11px] font-semibold hover:bg-black/90 transition-colors"
                            data-testid={`ai-apply-${i}-${j}`}
                          >
                            <CheckCircle2 size={11} /> Apply
                          </button>
                        </div>
                        <div className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{s.new_value}</div>
                        {s.rationale && (
                          <div className="mt-2 text-[11px] text-muted-kc italic">{s.rationale}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-surface-2 border border-kc px-4 py-3 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-kc animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-kc animate-pulse [animation-delay:120ms]" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-kc animate-pulse [animation-delay:240ms]" />
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="px-4 py-3 border-t border-kc bg-white">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask the assistant…"
              rows={2}
              className="flex-1 resize-none p-2 text-sm rounded-md border border-kc-strong focus:outline-none focus:border-[color:var(--kc-gold)]"
              data-testid="ai-assistant-input"
            />
            <button
              type="button"
              onClick={() => send()}
              disabled={loading || !draft.trim()}
              className="h-10 w-10 inline-flex items-center justify-center rounded-md bg-[color:var(--kc-gold)] text-black hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 transition-colors"
              aria-label="Send"
              data-testid="ai-assistant-send"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
          <div className="mt-1.5 text-[10px] text-muted-kc inline-flex items-center gap-1">
            <MessageSquare size={10} /> Enter to send · Shift+Enter for newline
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
